import { useCallback } from 'react';
import type { OnboardingApiClient } from '@/features/onboarding/api/onboarding-api-client';
import { SMS_RESEND_COUNTDOWN_SECONDS } from '@/features/onboarding/constants/onboarding.constants';
import {
  FactorType,
  type OnboardingState,
  type OnboardingFlowResponse,
} from '@/features/onboarding/types/onboarding.types';

interface UseAlternativeActionsParams {
  setState: React.Dispatch<React.SetStateAction<OnboardingState>>;
  processResponse: (response: OnboardingFlowResponse) => Promise<void>;
  api: OnboardingApiClient;
}

interface UseAlternativeActionsReturn {
  chooseAlternative: (factorType: FactorType) => Promise<boolean>;
  goBackToAlternatives: () => Promise<void>;
}

export function useAlternativeActions({
  setState,
  processResponse,
  api,
}: UseAlternativeActionsParams): UseAlternativeActionsReturn {
  /**
   * Reset state when switching factors (choosing alternative or going back)
   * Clears error state and retry flags to ensure the new factor isn't locked out
   */
  const resetFactorSwitchState = useCallback(() => {
    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
      hasExceededRetries: false,
    }));
  }, [setState]);

  /**
   * Choose an alternative authenticator when hasAlternative=true
   * Backend initiates the selected factor's challenge
   */
  const chooseAlternative = useCallback(
    async (factorType: FactorType) => {
      resetFactorSwitchState();

      try {
        const response = await api.chooseAuthenticator(factorType);
        await processResponse(response);

        // Reset OTP state when selecting an OTP-based factor
        // Backend sends a new OTP code, so we need fresh countdown and resend count
        const isOtpFactor =
          factorType === FactorType.SMS_OTP ||
          factorType === FactorType.WHATSAPP_OTP;

        if (isOtpFactor) {
          setState(prev => ({
            ...prev,
            smsResendCountdown: SMS_RESEND_COUNTDOWN_SECONDS,
            smsResendCount: 0,
          }));
        }

        return true;
      } catch (error) {
        setState(prev => ({
          ...prev,
          error:
            error instanceof Error
              ? error.message
              : 'Failed to choose authenticator',
          isLoading: false,
        }));

        return false;
      }
    },
    [api, setState, processResponse, resetFactorSwitchState]
  );

  /**
   * Go back to alternatives selection
   * Calls the backend /authenticator-alternatives API to fetch fresh options
   */
  const goBackToAlternatives = useCallback(async () => {
    resetFactorSwitchState();

    try {
      const altResponse = await api.getAuthenticatorAlternatives();
      const alternatives = altResponse.alternatives.map(alt => ({
        factorType: alt.factorType as FactorType, // Backend already returns factorType
        isEnrolled: alt.isEnrolled,
        displayName: alt.displayName,
        description: alt.description,
      }));

      setState(prev => ({
        ...prev,
        currentFactorType: null,
        currentStepMetadata: null,
        alternatives,
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to fetch alternatives',
        isLoading: false,
      }));
    }
  }, [api, setState, resetFactorSwitchState]);

  return { chooseAlternative, goBackToAlternatives };
}
